// V4.12 Phase F — Canvas scatter plot (Preis vs. km bzw. Preis vs. m²).
// Points computed server-side from the same search result set.
// V4.23 — interactive: every point opens its listing.
import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Cell, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis,
} from "recharts";

function fmtShort(v) {
  if (v == null) return "—";
  if (Math.abs(v) >= 1000000) return `${(v / 1000000).toFixed(1)}M`;
  if (Math.abs(v) >= 1000) return `${Math.round(v / 1000)}k`;
  return String(Math.round(v));
}

function colorFor(signal) {
  if (signal === "under_market" || signal === "strong_under_market") return "#10b981";
  if (signal === "over_market" || signal === "strongly_over_market") return "#f43f5e";
  return "#a1a1aa";
}

function ScatterTip({ active, payload, xLabel }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-lg border border-zinc-700 bg-zinc-950/95 px-2.5 py-1.5 text-xs">
      <div className="font-semibold text-amber-300">
        € {Math.round(p.y).toLocaleString("de-AT")}
      </div>
      <div className="text-zinc-400">{Math.round(p.x).toLocaleString("de-AT")} {xLabel}</div>
    </div>
  );
}

export function ScatterPlotWidget({ widget }) {
  const { t } = useTranslation("ai");
  const navigate = useNavigate();
  const points = widget.points || [];
  if (!points.length) return null;
  const isAuto = widget.x_key === "mileage_km";
  const title = isAuto ? t("widgets.scatterAuto") : t("widgets.scatterImmo");
  const xUnit = isAuto ? "km" : "m²";
  return (
    <div
      data-testid="ai-widget-scatter"
      className="rounded-xl border border-zinc-800 bg-zinc-900/70 p-3 space-y-2"
    >
      <div className="text-xs text-zinc-500 uppercase tracking-wide">
        {title}{widget.label ? ` · ${widget.label}` : ""}
      </div>
      <div className="h-48 sm:h-52">
        <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={170}>
          <ScatterChart margin={{ top: 8, right: 8, left: -14, bottom: 0 }}>
            <XAxis
              type="number"
              dataKey="x"
              domain={["auto", "auto"]}
              tick={{ fill: "#71717a", fontSize: 10 }}
              tickFormatter={fmtShort}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="number"
              dataKey="y"
              domain={["auto", "auto"]}
              tick={{ fill: "#71717a", fontSize: 10 }}
              tickFormatter={fmtShort}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={<ScatterTip xLabel={xUnit} />}
              cursor={{ strokeDasharray: "3 3", stroke: "#3f3f46" }}
            />
            <Scatter
              data={points}
              fillOpacity={0.75}
              isAnimationActive={false}
              onClick={(p) => p?.id && navigate(`/listing/${p.id}`)}
              className="cursor-pointer"
            >
              {points.map((p, i) => (
                <Cell key={i} fill={colorFor(p.signal)} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center gap-3 text-[10px] text-zinc-600">
        <span className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> {t("widgets.legendUnder")}
        </span>
        <span className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-zinc-400" /> {t("widgets.legendFair")}
        </span>
        <span className="flex items-center gap-1">
          <span className="h-1.5 w-1.5 rounded-full bg-rose-400" /> {t("widgets.legendOver")}
        </span>
        <span className="ml-auto">{points.length} {t("widgets.listingsWord")}</span>
      </div>
    </div>
  );
}
